import 'app/Window'

import { HttpError } from '@forto-dev/ui-platform-http'
import { Port } from 'features/Directory'
import { ManualQuoteDialogState, useManualQuoteDialogState } from 'features/ManualQuotes'
import { isArray, isNil, pickBy } from 'lodash-es'
import { createContext, FC, ReactNode, useCallback, useContext, useMemo } from 'react'
import { useRootStore } from 'service/useRootStore'
import { useI18n } from 'utils/localisation'

import { BookingMeta } from './Booking/BookingMeta'
import { IQuoteRequest, QuoteRequestJSON, serializeRequest } from './fromMobx'
import { IncoTermMetadata } from './Incoterms/incotermsMeta'
import { QuoteSearchPayload } from './QuoteRequest/quoteRequestUtils'
import { IQuoteSheet } from './QuoteSheet/QuoteSheetModel'
import { IQuoteOption } from './QuoteSheet/QuoteSheetOption'

const QuoteServiceContext = createContext<QuoteService | null>(null)

type QuoteServiceContextProviderProps = {
  children: ReactNode
}

type ManualQuoteType = 'HEAVY_CARGO' | 'DANGEROUS_GOODS' | 'INVALID_PORT' | 'default' | 'customMode' | 'INVALID_REQUEST'

export type QuoteRequestSuccessData = {
  quoteSheetId?: string
  requiresManualQuotation?: boolean
  manualQuotationReasons?: IManualQuoteReason[]
  type?: ManualQuoteType

  suggestedPort?: Port
  message?: string
}

export interface IManualQuoteReason {
  message: string
  code: string
  meta: unknown
}
/**
 * Composes url search string from the params object
 * Arrays are serialised as a multiple praramters (recommended approach)
 * Example: {foo: [1,2]} --> `?foo=1&foo=2`
 */
export const getURLSearchString = (params: object) => {
  const u = new URLSearchParams()
  Object.entries(params).forEach(([key, value]) => {
    if (isArray(value)) {
      value.map((item) => u.append(key, item))
    } else {
      u.append(key, value)
    }
  })
  return u.toString()
}

export type QuoteService = {
  recentSearches: () => Promise<IQuoteSheet[]>
  getIncotermsMetadata: () => Promise<IncoTermMetadata[]>
  search: (
    quoteRequest: IQuoteRequest | QuoteSearchPayload,
    noErrorAlert?: boolean,
    meta?: BookingMeta
  ) => Promise<QuoteRequestSuccessData>
  getQuoteOption: (optionID: string) => Promise<IQuoteOption>
  showManualQuotePopup: () => void
  searchRequest: (quoteRequestJSON: QuoteRequestJSON) => Promise<QuoteRequestSuccessData>
  mqDialogState: ManualQuoteDialogState
}

export const QuoteServiceContextProvider: FC<QuoteServiceContextProviderProps> = ({ children }) => {
  const { http, history, uiStore, manualQuoteStore, errorService } = useRootStore()
  const mqDialogState = useManualQuoteDialogState()
  const { translate } = useI18n()
  const loaderKey = 'QUOTE_SERVICE'

  const recentSearches = useCallback(async (): Promise<IQuoteSheet[]> => {
    return http.get<IQuoteSheet[]>('/v1/quotes/recentSearches')
  }, [http])

  const getIncotermsMetadata = useCallback(async (): Promise<IncoTermMetadata[]> => {
    return http.get<IncoTermMetadata[]>('/v1/quotes/incoterms')
  }, [http])

  const getQuoteOption = useCallback(
    async (optionID: string) => {
      return http.get<IQuoteOption>(`/v2/quoteService/quoteSheet/${optionID}`)
    },
    [http]
  )

  const searchRequest = useCallback(
    async (quoteRequestJSON: QuoteRequestJSON): Promise<QuoteRequestSuccessData> => {
      return http.post('/v1/quotes/search', quoteRequestJSON)
    },
    [http]
  )

  const processWithQuoteResults = useCallback(
    (data: QuoteRequestSuccessData, quoteRequest: IQuoteRequest, meta?: BookingMeta) => {
      const quoteRequestJSON = serializeRequest(quoteRequest)

      if (data.requiresManualQuotation) {
        mqDialogState.triggerManualQuoteDialog(quoteRequest, data.quoteSheetId, data.type, data.manualQuotationReasons)

        window.dataLayer.push({
          event: 'quoteRequest',
          eventCategory: 'Quote Request',
          eventAction: 'Manual',
        })
      } else {
        window.dataLayer.push({
          event: 'quoteRequest',
          eventCategory: 'Quote Request',
          eventAction: 'Realtime',
        })
        history.push({
          pathname: '/quote-sheet/search',
          search: getURLSearchString({
            request: JSON.stringify(pickBy(quoteRequestJSON, (val) => !isNil(val))),
            ...meta,
          }),
          state: { meta, quoteSheetId: data.quoteSheetId },
        })
      }
    },
    [history, manualQuoteStore, mqDialogState]
  )

  const successHandler = useCallback(
    (quoteRequest: IQuoteRequest, meta?: BookingMeta) => (data: QuoteRequestSuccessData) => {
      // Type should only be present when an error ocurre
      if (data.type && !data.requiresManualQuotation) {
        // If type is present, it means that something went wrong.
        // But if we have a quotesheet id it means that we can still create
        // Manual Quotation
        // The type tells us what went wrong
        uiStore.loader.hide(loaderKey)
        return data
      }
      if (data.quoteSheetId) {
        processWithQuoteResults(data, quoteRequest, meta)
        // loader hide should be after processing
        // because processing can do history.push
        // if loader.hide will be before, it triggers update of current component if it uses lader.isLoading
        // see Shipment smart component
        uiStore.loader.hide(loaderKey)
      } else {
        throw new Error('Unexpected response from the server')
      }
    },
    [processWithQuoteResults, uiStore]
  )

  const responseErrorHandler = useCallback(
    (_quoteRequest: IQuoteRequest, noErrorAlert?: boolean) => (err: HttpError) => {
      uiStore.loader.hide()
      if (!err.message) {
        // eslint-disable-next-line no-param-reassign
        err.message = translate('quote_request-server_error')
      }
      if (!noErrorAlert && err instanceof HttpError) {
        errorService.showErrorDialog(err)
      }
      return Promise.reject(err)
    },
    [errorService, translate, uiStore]
  )

  const search = useCallback(
    async (quoteRequest: IQuoteRequest, noErrorAlert?: boolean, meta?: BookingMeta) => {
      history.push({ pathname: '/quote-request/search' })
      const quoteRequestJSON = serializeRequest(quoteRequest)
      uiStore.loader.show({ key: loaderKey, showText: true })

      return searchRequest(quoteRequestJSON)
        .then(successHandler(quoteRequest, meta), responseErrorHandler(quoteRequest, noErrorAlert))
        .catch((err) => {
          return Promise.reject(err)
        })
    },
    [history, responseErrorHandler, searchRequest, successHandler, uiStore]
  )

  const showManualQuotePopup = useCallback(() => {
    manualQuoteStore.status = 'inprogress'
    manualQuoteStore.shown = true
  }, [manualQuoteStore])

  const quoteService = useMemo(
    () => ({
      recentSearches,
      getIncotermsMetadata,
      search,
      getQuoteOption,
      showManualQuotePopup,
      searchRequest,
      mqDialogState,
    }),
    [getIncotermsMetadata, recentSearches, search, getQuoteOption, showManualQuotePopup, searchRequest, mqDialogState]
  )

  // @ts-expect-error TS(2322) FIXME: Type '{ recentSearches: () => Promise<IQuoteSheet[... Remove this comment to see the full error message
  return <QuoteServiceContext.Provider value={quoteService}>{children}</QuoteServiceContext.Provider>
}

export const useQuoteService = (): QuoteService => {
  const quoteService = useContext(QuoteServiceContext)
  if (!quoteService) {
    throw new Error('useQuoteService must be used within a QuoteServiceContextProvider')
  }
  return quoteService
}
